import { useMutation, useQuery } from '@vue/apollo-composable';
import { useDocumentVisibility } from '@vueuse/core';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';

import {
  EnterOrganizationAsOwnerDocument,
  ForceStopPlatformImpersonationSessionDocument,
  GetPlatformActivityFeedDocument,
  type GetPlatformActivityFeedQueryVariables,
  GetPlatformImpersonationActionsDocument,
  type GetPlatformImpersonationActionsQueryVariables,
  GetPlatformImpersonationSessionsDocument,
  type GetPlatformImpersonationSessionsQueryVariables,
  GetPlatformOrganizationDocument,
  type GetPlatformOrganizationQueryVariables,
  GetPlatformOrganizationsDocument,
  type GetPlatformOrganizationsQueryVariables,
  GetPlatformOverviewDocument,
  type GetPlatformOverviewQueryVariables,
  GetPlatformQueueStatsDocument,
  GetPlatformUserDocument,
  type GetPlatformUserQueryVariables,
  GetPlatformUsersDocument,
  type GetPlatformUsersQueryVariables,
  SetPlatformOrganizationModulesDocument,
  StartPlatformImpersonationDocument,
  StopPlatformImpersonationDocument,
} from '@shared/api/graphql/gql/graphql';

import { platformContext, platformMutationContext } from './platform-context';

export const usePlatformOrganizationsQuery = (
  variables: MaybeRefOrGetter<GetPlatformOrganizationsQueryVariables>,
  enabled: MaybeRefOrGetter<boolean> = true,
) =>
  useQuery(
    GetPlatformOrganizationsDocument,
    variables,
    computed(() => ({ ...platformContext, enabled: toValue(enabled) })),
  );

export const usePlatformUsersQuery = (
  variables: MaybeRefOrGetter<GetPlatformUsersQueryVariables>,
  enabled: MaybeRefOrGetter<boolean> = true,
) =>
  useQuery(
    GetPlatformUsersDocument,
    variables,
    computed(() => ({ ...platformContext, enabled: toValue(enabled) })),
  );

export const usePlatformUserQuery = (variables: MaybeRefOrGetter<GetPlatformUserQueryVariables>) =>
  useQuery(GetPlatformUserDocument, variables, platformContext);

export const usePlatformOrganizationQuery = (variables: MaybeRefOrGetter<GetPlatformOrganizationQueryVariables>) =>
  useQuery(GetPlatformOrganizationDocument, variables, platformContext);

export const usePlatformImpersonationSessionsQuery = (
  variables: MaybeRefOrGetter<GetPlatformImpersonationSessionsQueryVariables>,
) => useQuery(GetPlatformImpersonationSessionsDocument, variables, platformContext);

export const usePlatformImpersonationActionsQuery = (
  variables: MaybeRefOrGetter<GetPlatformImpersonationActionsQueryVariables>,
  enabled: MaybeRefOrGetter<boolean>,
) =>
  useQuery(
    GetPlatformImpersonationActionsDocument,
    variables,
    computed(() => ({
      ...platformContext,
      enabled: toValue(enabled),
    })),
  );

/**
 * Опрос штаба. data-refresh runtime работает только внутри организации и на hq
 * ничего не перечитает, поэтому интервал задаётся здесь; скрытая вкладка не опрашивает.
 */
const usePlatformPolling = (intervalMs: number) => {
  const visibility = useDocumentVisibility();

  return computed(() => ({
    ...platformContext,
    fetchPolicy: 'cache-and-network' as const,
    pollInterval: visibility.value === 'visible' ? intervalMs : 0,
  }));
};

export const usePlatformOverviewQuery = (variables: MaybeRefOrGetter<GetPlatformOverviewQueryVariables>) =>
  useQuery(GetPlatformOverviewDocument, variables, usePlatformPolling(60_000));

export const usePlatformActivityFeedQuery = (variables: MaybeRefOrGetter<GetPlatformActivityFeedQueryVariables>) =>
  useQuery(GetPlatformActivityFeedDocument, variables, usePlatformPolling(5_000));

export const usePlatformQueueStatsQuery = () =>
  useQuery(GetPlatformQueueStatsDocument, null, usePlatformPolling(30_000));

export const useStartPlatformImpersonationMutation = () =>
  useMutation(StartPlatformImpersonationDocument, platformMutationContext);

export const useStopPlatformImpersonationMutation = () =>
  useMutation(StopPlatformImpersonationDocument, platformMutationContext);

export const useEnterOrganizationAsOwnerMutation = () =>
  useMutation(EnterOrganizationAsOwnerDocument, platformMutationContext);

export const useForceStopPlatformImpersonationSessionMutation = () =>
  useMutation(ForceStopPlatformImpersonationSessionDocument, platformMutationContext);

export const useSetPlatformOrganizationModulesMutation = () =>
  useMutation(SetPlatformOrganizationModulesDocument, platformContext);
